/**
 * Modal in-app qui rappelle à l'agent de saisir son rapport journalier.
 * S'affiche automatiquement quand :
 *  - l'utilisateur a un rôle éligible (agent, admin)
 *  - l'heure locale ≥ seuil du backend (17h par défaut)
 *  - le rapport du jour est absent OU vide (aucun résumé/difficulté/besoin)
 *  - l'utilisateur n'a pas snoozé (localStorage 2h)
 *
 * Cette modale complète le push notification côté serveur : le push
 * apparaît hors app, la modale prend le relais dès qu'il ouvre l'app.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, FileText, ArrowRight, Clock } from 'lucide-react';
import type { RootState } from '../../store';
import {
  useMonRappelQuery, useSaveRapportMutation,
} from '../../services/serviceScolariteApi';

const SNOOZE_KEY = 'service_scolarite_rappel_rapport_snooze_until';

export const RappelRapportJournalierModal: React.FC = () => {
  const user = useSelector((s: RootState) => s.auth.user) as any;
  const token = useSelector((s: RootState) => s.auth.token);
  const isDark = useSelector((s: RootState) => s.ui.theme) === 'dark';
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [resume, setResume] = useState('');
  const [saving, setSaving] = useState(false);

  const canReceive = !!user && !!token
    && ['super_admin', 'admin', 'agent'].includes(user?.role);
  const { data, refetch } = useMonRappelQuery(undefined, { skip: !canReceive });
  const [saveRapport] = useSaveRapportMutation();

  useEffect(() => {
    if (!canReceive || !data) return;
    if (!data.doit_faire) return;

    // Snooze ?
    const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() < snoozeUntil) return;

    // Heure locale > seuil (17h par défaut) ?
    const h = new Date().getHours();
    if (h < (data.seuil_heure || 17)) return;

    setOpen(true);
  }, [canReceive, data]);

  const snooze = () => {
    // Avertissement clair : sans rapport, le compte sera bloqué demain matin.
    const ok = window.confirm(
      "⚠ Attention\n\n"
      + "Si vous ne rédigez pas votre rapport aujourd'hui, votre compte sera "
      + "AUTOMATIQUEMENT VERROUILLÉ pendant 72 heures à partir de demain matin.\n\n"
      + "Toutes les autres pages seront inaccessibles jusqu'à la rédaction du "
      + "rapport manquant.\n\n"
      + "Voulez-vous vraiment reporter la rédaction de 2 h ?"
    );
    if (!ok) return;
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + 2 * 60 * 60 * 1000)); // 2h
    setOpen(false);
  };

  const enregistrerRapide = async () => {
    if (!resume.trim()) return;
    setSaving(true);
    try {
      await saveRapport({ resume_libre: resume.trim() }).unwrap();
      setResume('');
      setOpen(false);
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const openFullPage = () => {
    setOpen(false);
    navigate('/service-scolarite?tab=mon-rapport');
  };

  if (!open || !data) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 p-3">
      <div className={`rounded-xl w-full max-w-md shadow-2xl border-2 ${
        isDark ? 'bg-gray-900 border-amber-600' : 'bg-white border-amber-500'
      }`}>
        <header className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 rounded-t-xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
              <FileText size={16} />
            </div>
            <div>
              <h3 className="font-bold text-sm text-amber-800 dark:text-amber-200">
                Rapport journalier attendu
              </h3>
              <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                Prenez 30 secondes pour informer le responsable.
              </p>
            </div>
          </div>
          <button onClick={snooze} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            title="Me rappeler dans 2 h">
            <X size={16} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          {data.nb_taches_du_jour > 0 ? (
            <div className={`rounded-lg p-2 text-xs ${
              isDark ? 'bg-emerald-900/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
            }`}>
              Vous avez terminé <strong>{data.nb_taches_du_jour}</strong> tâche{data.nb_taches_du_jour > 1 ? 's' : ''} aujourd'hui —
              elles seront automatiquement listées dans le rapport.
            </div>
          ) : (
            <div className={`rounded-lg p-2 text-xs ${
              isDark ? 'bg-amber-900/20 text-amber-300' : 'bg-amber-50 text-amber-700'
            }`}>
              <AlertTriangle size={11} className="inline mr-1" />
              Aucune tâche cochée aujourd'hui. Rédigez au moins un résumé rapide.
            </div>
          )}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1 block">
              Résumé rapide de la journée
            </label>
            <textarea
              value={resume}
              onChange={(e) => setResume(e.target.value)}
              rows={4}
              autoFocus
              placeholder="Ex : Réception de 12 nouveaux dossiers, appels aux 8 étudiants en retard, préparation des enveloppes examens…"
              className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${
                isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
              }`}
            />
          </div>
        </div>

        <footer className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-between gap-2">
          <button onClick={snooze}
            className={`inline-flex items-center gap-1 px-3 py-2 text-xs rounded-md ${
              isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <Clock size={12} /> Dans 2 h
          </button>
          <div className="flex gap-2">
            <button onClick={openFullPage}
              className={`inline-flex items-center gap-1 px-3 py-2 text-xs rounded-md ${
                isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              Détaillé <ArrowRight size={12} />
            </button>
            <button onClick={enregistrerRapide} disabled={saving || !resume.trim()}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-md font-semibold disabled:opacity-50"
            >
              <FileText size={12} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default RappelRapportJournalierModal;
